"use client";

import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getPublicTemplates, Template } from "@/lib/api";
import { Search, Filter, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { TemplateDetailSheet } from "@/components/templates/template-detail-sheet";
import { TemplateCard } from "@/components/templates/template-card";
import { PromptLibraryShowcase } from "@/miniapp/features/templates/PromptLibraryShowcase";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { getModelById } from "@/lib/model-helpers";
import { cn } from "@/lib/utils";
import { isPromptLibraryIdeaTemplate, resolveTemplateCreateMode } from "@/lib/template-library";

type SortOption = "newest" | "oldest" | "az" | "za";

// Single-shot fetch like the miniapp's TemplatesPanel — paginating in 100s
// caused the showcase to thrash on mobile (each chunk re-rendered ~70 cards
// across 9 shelves, and users perceived this as «карточки бесконечно дрыгаются
// и лоудер»). 500 covers the catalog with margin and matches the miniapp's
// behavior, so the two views stay in sync.
const TEMPLATES_FETCH_LIMIT = 500;

export default function LibraryView({ initialTemplates = [] }: { initialTemplates?: Template[] }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [templates, setTemplates] = useState<Template[]>(initialTemplates);
  const [loading, setLoading] = useState(initialTemplates.length === 0);
  const [loadError, setLoadError] = useState(false);
  const loadRunRef = useRef(0);

  // Filter States — initialize from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [filterType, setFilterType] = useState<string>(searchParams.get("type") || "all");
  const [selectedModel, setSelectedModel] = useState<string>(searchParams.get("model") || "all");
  const [sortOption, setSortOption] = useState<SortOption>((searchParams.get("sort") as SortOption) || "newest");
  
  // Detail State
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Sync filter state → URL search params, but only when the resulting URL
  // would actually change. The previous version called `router.replace` on
  // every mount even when nothing was filtered, which round-tripped
  // useSearchParams and added an extra render cycle on every filter change.
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set("q", searchQuery);
    if (filterType !== "all") params.set("type", filterType);
    if (selectedModel !== "all") params.set("model", selectedModel);
    if (sortOption !== "newest") params.set("sort", sortOption);
    const qs = params.toString();
    const target = `/prompt-library${qs ? `?${qs}` : ""}`;
    const current = `${window.location.pathname}${window.location.search}`;
    if (target === current) return;
    router.replace(target, { scroll: false });
  }, [searchQuery, filterType, selectedModel, sortOption, router]);

  useEffect(() => {
    if (initialTemplates.length === 0) loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = async () => {
    const runId = ++loadRunRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const all = await getPublicTemplates({
        status: "published",
        limit: TEMPLATES_FETCH_LIMIT,
        source: "auto",
      });
      if (loadRunRef.current !== runId) return;
      setTemplates(all.filter(isPromptLibraryIdeaTemplate));
    } catch (e: unknown) {
      if (loadRunRef.current !== runId) return;
      console.error(e);
      setLoadError(true);
      toast.error("Не удалось загрузить шаблоны");
    } finally {
      if (loadRunRef.current === runId) setLoading(false);
    }
  };

  // Derive unique models from templates
  const availableModels = useMemo(() => {
    const models = new Set<string>();
    templates.forEach(t => {
      if (t.default_model) models.add(t.default_model);
    });
    return Array.from(models).map(id => {
      const model = getModelById(id);
      return {
        id,
        label: model?.name || id
      };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [templates]);

  // Filter & Sort Logic
  const filteredTemplates = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const result = templates.filter(t => {
      if (query) {
        const searchable = [
          t.title,
          t.description,
          t.prompt?.base,
          t.default_model,
          t.model_id,
          getModelById(t.default_model || "")?.name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!query.split(" ").every(token => searchable.includes(token))) return false;
      }
      const matchesType = filterType === "all" || t.type === filterType;
      const matchesModel = selectedModel === "all" || t.default_model === selectedModel;

      return matchesType && matchesModel;
    });

    // Sorting
    result.sort((a, b) => {
      switch (sortOption) {
        case "newest":
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        case "oldest":
          return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
        case "az":
          return a.title.localeCompare(b.title);
        case "za":
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    });

    return result;
  }, [templates, searchQuery, filterType, selectedModel, sortOption]);

  // Memoize the card-row handlers so the memoized TemplateCard children don't
  // re-render whenever LibraryView's filter state changes. Without
  // useCallback the handler references churn on every keystroke and the
  // showcase re-renders every card across all 9 shelves.
  const handleUseTemplate = useCallback((template: Template) => {
    const normalizedType = (template.type || "").toLowerCase();
    // Higgsfield motion effects (external + native) launch via the
    // TemplatesPanel effect dialog (photo upload + start). LandingApp
    // picks up the `?effect=<id>` query and switches view to
    // 'templates' with initialEffectId, which auto-opens the launch
    // dialog. Routing them through /image-generation as a regular
    // template just swapped the prompt into Nano Banana — wrong app.
    if (normalizedType === "motion") {
      router.push(`/?effect=${encodeURIComponent(template.id)}`);
      return;
    }

    const mode = resolveTemplateCreateMode(template);
    let targetPath = "/image-generation";
    if (mode === "video") targetPath = "/video-generation";
    if (mode === "photoshoot") targetPath = "/photoshoot";

    router.push(`${targetPath}?template=${template.id}`);
  }, [router]);

  const openDetail = useCallback((template: Template) => {
    setSelectedTemplate(template);
    setIsDetailOpen(true);
  }, []);

  const handleModelClick = useCallback((modelId: string) => {
    setSelectedModel(modelId);
  }, []);

  const clearFilters = () => {
    setSearchQuery("");
    setFilterType("all");
    setSelectedModel("all");
    setSortOption("newest");
  };

  return (
    <div className="container max-w-[1600px] py-8 space-y-8 min-h-screen flex flex-col">

      {/* Content Wrapper. transition-opacity (NOT transition-all) because
          the only thing toggling here is opacity when the detail sheet
          opens — `transition-all` was animating every CSS-property change
          (incl. layout) which on mobile compounded with image-lazy-load to
          cause a constant «дрыгание» perception. */}
      <div className={`transition-opacity duration-300 space-y-6 ${isDetailOpen ? 'opacity-40 pointer-events-none' : ''}`}>

      {/* Header + Search + Filters — compact */}
      <div className="flex flex-col gap-4 pt-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Готовые промты для нейросети</h2>
          <span className="text-sm text-muted-foreground">{!loading && filteredTemplates.length}</span>
        </div>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Библиотека готовых промтов для нейросетей — для фотосессии, портретов, генерации изображений и видео.
          Выберите шаблон, скопируйте промпт и сгенерируйте результат бесплатно.
        </p>

        <div className="relative w-full sm:max-w-md">
           <Search className="absolute left-3 top-2.5 sm:top-2.5 h-4 w-4 text-muted-foreground" />
           <Input
             placeholder="Поиск..."
             className="pl-9 h-10 text-sm bg-background border-border/50 rounded-lg"
             value={searchQuery}
             onChange={(e) => setSearchQuery(e.target.value)}
           />
        </div>

        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            {(["all", "image", "video", "motion", "photoshoot"] as const).map((type) => (
                <Button
                    key={type}
                    variant={filterType === type ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setFilterType(type)}
                    className={cn("h-9 sm:h-8 rounded-lg px-3 text-xs", filterType !== type && "text-muted-foreground")}
                >
                    {{ all: "Все", image: "Изображения", video: "Видео", motion: "Эффекты", photoshoot: "Фотосессия" }[type]}
                </Button>
            ))}

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 rounded-lg px-3 text-xs text-muted-foreground gap-1.5">
                        <Filter className="w-3 h-3" />
                        {selectedModel === "all" ? "Модель" : availableModels.find(m => m.id === selectedModel)?.label}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52 max-h-[300px] overflow-y-auto">
                    <DropdownMenuCheckboxItem checked={selectedModel === "all"} onCheckedChange={() => setSelectedModel("all")}>Все модели</DropdownMenuCheckboxItem>
                    <DropdownMenuSeparator />
                    {availableModels.map(model => (
                        <DropdownMenuCheckboxItem key={model.id} checked={selectedModel === model.id} onCheckedChange={() => setSelectedModel(model.id)}>{model.label}</DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 rounded-lg px-3 text-xs text-muted-foreground gap-1.5">
                        <ArrowUpDown className="w-3 h-3" />
                        {sortOption === "newest" ? "Новые" : "Старые"}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuCheckboxItem checked={sortOption === "newest"} onCheckedChange={() => setSortOption("newest")}>Новые</DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem checked={sortOption === "oldest"} onCheckedChange={() => setSortOption("oldest")}>Старые</DropdownMenuCheckboxItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
      </div>

      {/* Templates Grid */}
      {loadError && !loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <h3 className="text-lg font-semibold">Не удалось загрузить</h3>
            <p className="text-muted-foreground text-sm mt-1">Проверьте подключение и попробуйте снова</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={loadTemplates}>
                Повторить
            </Button>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
            {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="flex flex-col overflow-hidden rounded-xl border border-border/30 bg-card shadow-sm">
                    <div className="relative aspect-[4/5] w-full bg-muted/20 animate-pulse">
                        <div className="absolute top-2 left-2 flex gap-1.5">
                            <Skeleton className="h-5 w-12 rounded-full" />
                            <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                        <div className="absolute bottom-3 left-3 right-3 space-y-1.5">
                            <Skeleton className="h-4 w-4/5" />
                            <Skeleton className="h-3 w-3/5" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <Search className="h-10 w-10 text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold">Ничего не найдено</h3>
            <p className="text-muted-foreground text-sm mt-1">Измените запрос или фильтры</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>
                Сбросить
            </Button>
        </div>
      ) : !searchQuery ? (
        // Render the shelf-based showcase whenever the user isn't searching
        // — type/model filters still feed `filteredTemplates`, so picking
        // «Изображения» just narrows the shelf contents instead of nuking
        // the shelves themselves (user reported «нижние подборки исчезают»
        // when they tapped a top filter chip).
        <PromptLibraryShowcase
            templates={filteredTemplates}
            onPreview={openDetail}
            onUse={handleUseTemplate}
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
            {filteredTemplates.map(t => (
                <TemplateCard
                    key={t.id}
                    template={t}
                    onPreview={openDetail}
                    onUse={handleUseTemplate}
                    onModelClick={handleModelClick}
                />
            ))}
        </div>
      )}

      </div> {/* End Content Wrapper */}

      {selectedTemplate && (
        <TemplateDetailSheet 
          template={selectedTemplate} 
          open={isDetailOpen} 
          onOpenChange={setIsDetailOpen}
          onUse={handleUseTemplate}
        />
      )}
    </div>
  );
}
